import { formattedDateGenerator } from "../../../utils/updateSearchParamValues";
import { convertHHMMTime } from "../../Alerts/AlertsUtils/convertHHMMTime";

const VALID_TABS = ["ailink", "sentinel", "smtp", "frontel"] as const;
export type ClipsTabParam = typeof VALID_TABS[number];

const parseNumberList = (str: string | null): number[] => {
  if (!str || !str.trim()) return [];
  return str
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n));
};

/** Parse "Jan-06-2026" (Month-DD-YYYY) to Date */
export const parseDateFromParam = (str: string | null): Date | null => {
  if (!str || !str.trim()) return null;
  const monthNames: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const parts = str.trim().split("-");
  if (parts.length !== 3) return null;
  const [monthStr, dayStr, yearStr] = parts;
  const month = monthNames[monthStr];
  if (month === undefined) return null;
  const day = parseInt(dayStr, 10);
  const year = parseInt(yearStr, 10);
  if (Number.isNaN(day) || Number.isNaN(year)) return null;
  const date = new Date(year, month, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

/** Parse HHMM string (e.g. "1430") to Date (today with that time) */
export const parseTimeFromParam = (str: string | null): Date | null => {
  if (!str || !str.trim() || str.length < 4) return null;
  try {
    const dateStr = convertHHMMTime(str.trim());
    return new Date(dateStr);
  } catch {
    return null;
  }
};

export interface ParsedClipsSearchParams {
  tab: ClipsTabParam | null;
  user: string | null;
  group: string | null;
  file_id: string | null;
  clip_id: string | null;
  siteIds: number[];
  cameraIds: number[];
  start_date: Date | null;
  end_date: Date | null;
  start_time: Date | null;
  end_time: Date | null;
  clip_result: string | null;
  ground_truth: string | null;
  hasAnyFilterParam: boolean;
}

export const parseClipsSearchParams = (
  searchParams: URLSearchParams
): ParsedClipsSearchParams => {
  const tabRaw = searchParams.get("tab");
  const tab =
    tabRaw && VALID_TABS.includes(tabRaw as ClipsTabParam)
      ? (tabRaw as ClipsTabParam)
      : null;

  const user = searchParams.get("user") || null;
  const group = searchParams.get("group") || null;
  const file_id = searchParams.get("file_id") || null;
  const clip_id = searchParams.get("clip_id") || null;
  const siteIds = parseNumberList(searchParams.get("site"));
  const cameraIds = parseNumberList(searchParams.get("camera"));
  const start_date = parseDateFromParam(searchParams.get("start_date"));
  const end_date = parseDateFromParam(searchParams.get("end_date"));
  const start_time = parseTimeFromParam(searchParams.get("start_time"));
  const end_time = parseTimeFromParam(searchParams.get("end_time"));
  const clip_result = searchParams.get("clip_result") || null;
  const ground_truth = searchParams.get("ground_truth") || null;

  const hasAnyFilterParam =
    !!user ||
    !!group ||
    siteIds.length > 0 ||
    cameraIds.length > 0 ||
    !!start_date ||
    !!end_date ||
    !!start_time ||
    !!end_time ||
    !!clip_result ||
    !!ground_truth;

  return {
    tab,
    user,
    group,
    file_id,
    clip_id,
    siteIds,
    cameraIds,
    start_date,
    end_date,
    start_time,
    end_time,
    clip_result,
    ground_truth,
    hasAnyFilterParam,
  };
};

/** Filter object shape shared across tabs: may have user, site_ids, camera_ids, dates, times, ground_truth, clip_result, groupId */
export interface ClipsFiltersForUrl {
  user?: string;
  site_ids?: number[] | string[];
  camera_ids?: number[] | string[];
  start_date?: Date | null;
  end_date?: Date | null;
  start_time?: Date | null;
  end_time?: Date | null;
  ground_truth?: string | null;
  clip_result?: string | null;
}

const toHHMM = (t: Date | null | undefined): string => {
  if (!t) return "";
  const d = t instanceof Date ? t : new Date(t);
  if (Number.isNaN(d.getTime())) return "";
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  return `${h}${m}`;
};

const toRoundedMinute = (date: Date): Date => {
  const rounded = new Date(date.getTime());
  rounded.setSeconds(0, 0);
  return rounded;
};

const getClipDateFromRow = (row: any): Date | null => {
  if (!row) return null;
  const rawTimestamp =
    row.clip_received_local ??
    row.clip_header_received ??
    row.clip_received ??
    row.local_time_received ??
    row.formatted_date;
  if (!rawTimestamp) return null;

  if (typeof rawTimestamp === "string") {
    const isoLikeMatch = rawTimestamp.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?/
    );
    if (isoLikeMatch) {
      const year = Number(isoLikeMatch[1]);
      const month = Number(isoLikeMatch[2]) - 1;
      const day = Number(isoLikeMatch[3]);
      const hour = Number(isoLikeMatch[4]);
      const minute = Number(isoLikeMatch[5]);
      const second = Number(isoLikeMatch[6] ?? 0);
      const millisecond = Number((isoLikeMatch[7] ?? "0").slice(0, 3));
      const fromIso = new Date(
        year,
        month,
        day,
        hour,
        minute,
        second,
        millisecond
      );
      return Number.isNaN(fromIso.getTime()) ? null : fromIso;
    }
  }

  const parsed = new Date(rawTimestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const removeClipsIdParamsFromUrl = (
  setSearchParams: (params: URLSearchParams) => void,
  searchParams: URLSearchParams
): void => {
  const currentFileId = searchParams.get("file_id");
  const currentClipId = searchParams.get("clip_id");
  if (!currentFileId && !currentClipId) return;
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete("file_id");
  nextParams.delete("clip_id");
  setSearchParams(nextParams);
};

export const buildShareClipUrl = (
  tab: ClipsTabParam,
  selectedRow: any,
  searchParams: URLSearchParams
): string | null => {
  const clipDate = getClipDateFromRow(selectedRow);
  if (!clipDate) {
    return null;
  }

  const selectedRowId =
    tab === "smtp" ? selectedRow?.clip_id ?? null : selectedRow?.file_id ?? null;
  if (!selectedRowId) {
    return null;
  }

  const roundedStart = toRoundedMinute(clipDate);
  const roundedEnd = new Date(roundedStart.getTime());
  roundedEnd.setMinutes(roundedEnd.getMinutes() + 1);

  const shareParams = new URLSearchParams(searchParams);
  shareParams.set("tab", tab);
  shareParams.set("start_date", formattedDateGenerator(roundedStart));
  shareParams.set("end_date", formattedDateGenerator(roundedStart));
  shareParams.set("start_time", toHHMM(roundedStart));
  shareParams.set("end_time", toHHMM(roundedEnd));

  if (tab === "smtp") {
    shareParams.set("clip_id", String(selectedRowId));
    shareParams.delete("file_id");
  } else {
    shareParams.set("file_id", String(selectedRowId));
    shareParams.delete("clip_id");
  }

  if (typeof window === "undefined") return `/clips?${shareParams.toString()}`;
  return `${window.location.origin}${window.location.pathname}?${shareParams.toString()}`;
};

export const updateClipsSearchParams = (
  setSearchParams: (params: URLSearchParams) => void,
  tab: ClipsTabParam,
  filters: ClipsFiltersForUrl,
  options?: {
    groupId?: number | null;
    selectedFileId?: string | null;
    selectedClipId?: string | null;
  }
): void => {
  const params = new URLSearchParams();

  params.set("tab", tab);

  if (options?.groupId != null) {
    params.set("group", String(options.groupId));
  } else {
    params.delete("group");
  }

  if (filters.user) {
    params.set("user", filters.user);
  } else {
    params.delete("user");
  }

  const siteIds = filters.site_ids;
  if (siteIds && siteIds.length > 0) {
    const first = siteIds[0];
    params.set(
      "site",
      Array.isArray(siteIds) ? siteIds.map(String).join(",") : String(first)
    );
  } else {
    params.delete("site");
  }

  const cameraIds = filters.camera_ids;
  if (cameraIds && cameraIds.length > 0) {
    params.set(
      "camera",
      Array.isArray(cameraIds)
        ? cameraIds.map(String).join(",")
        : String(cameraIds[0])
    );
  } else {
    params.delete("camera");
  }

  if (filters.start_date) {
    params.set("start_date", formattedDateGenerator(filters.start_date));
  } else {
    params.delete("start_date");
  }
  if (filters.end_date) {
    params.set("end_date", formattedDateGenerator(filters.end_date));
  } else {
    params.delete("end_date");
  }

  const startTimeStr = toHHMM(filters.start_time ?? undefined);
  if (startTimeStr) params.set("start_time", startTimeStr);
  else params.delete("start_time");
  const endTimeStr = toHHMM(filters.end_time ?? undefined);
  if (endTimeStr) params.set("end_time", endTimeStr);
  else params.delete("end_time");

  if (filters.clip_result) {
    params.set("clip_result", filters.clip_result);
  } else {
    params.delete("clip_result");
  }
  if (filters.ground_truth) {
    params.set("ground_truth", filters.ground_truth);
  } else {
    params.delete("ground_truth");
  }

  if (tab === "smtp") {
    if (options?.selectedClipId) {
      params.set("clip_id", options.selectedClipId);
    } else {
      params.delete("clip_id");
    }
    params.delete("file_id");
  } else {
    if (options?.selectedFileId) {
      params.set("file_id", options.selectedFileId);
    } else {
      params.delete("file_id");
    }
    params.delete("clip_id");
  }

  setSearchParams(params);
};
